/**
 * mastersWriteReach.ts — ONE predicate for the masters read/write split, and
 * the refusal that stops it reporting success over a row nothing moved.
 *
 * THE FACT. The live-module switch — `client.ts`, the estate's sole read site
 * for it, which is why this file names neither the variable nor a port — flips
 * a module's READS to `/graphql` per module, not per method. At CU-1a the
 * masters reads went live; only six
 * masters WRITES followed (vendorCreate, partyCategoryCreate,
 * partyCategorySetActive, vendorContactAdd, vendorBankAccountAdd,
 * vendorBankAccountSetDefault). Every other masters write still calls
 * `apiClient.{post,put,patch,delete}`, and in the browser-proof boot
 * (`yarn dev:mock`) MSW answers it. So the write lands in the mock store and
 * the next read comes back off Postgres, unmoved.
 *
 * AND IT DOES NOT 404. `mocks/data/*` is the behavioural spec;
 * `scripts/parity/export-fixtures.ts` canonicalises it into
 * `scripts/parity/fixtures/*.json`; every `persistence.seed.ts` seeds Postgres
 * from THOSE. A seeded row therefore exists in both stores under the same
 * primary key, the MSW handler's "does this row exist" gate answers YES, and
 * the write returns 200. No throw, no 4xx, no red pixel — the caller's
 * `.catch` never runs and the success toast is a fiction. Only a Postgres-only
 * row (created live after the seed) reaches the handler's 404 branch, which is
 * why the 404 measured by curling the branch server directly is NOT what the
 * app sees. Named hazard H-1,
 * `senditship-fe-be-drift-register-v2-2026-08-08.md` §0g-quater.
 *
 * WHY A REFUSAL RATHER THAN A LIVE LEG. The SDL already declares the roots
 * (`vendorUpdate`, `vendorSetActive`, `customerCreate`, `customerSetActive`,
 * `masterDataContact*`, `vendorLocation*`, `customerLocation*`, …) but no
 * operation document exists for any of them, and authoring ~30 documents plus
 * their mappers and shadow-diff runs is the remainder of the masters flip — a
 * wave, not a residual. What CAN be closed now is the lie: a write that cannot
 * be read back is not a degraded write, it is a no-op with a success toast.
 * This is the second branch of H-1's own removal trigger — "each remaining
 * REST write refuses like the vendor-line group".
 *
 * THE SHAPE IS THE PRODUCTS ONE. `vendorLineWriteReachesTheRead()` in
 * `product.api.ts` closed the identical split for the three product
 * vendor-line writes at `89ccd00`/`e2af933`. This is that predicate with a
 * different module key, stated as a CAPABILITY so a component may read it
 * without learning about transport.
 *
 * REMOVAL TRIGGER: a masters write rides `gqlMutate` — then it no longer calls
 * this, and `liveFacadeWriteParity.test.ts` §D stops counting it. The predicate
 * itself retires when the count reaches zero.
 */
import { isLiveModule } from './client';

/**
 * Can a masters write that still rides REST be SEEN by the next read?
 *
 * False exactly when the masters reads are graph-served, because that is when
 * the two seams part company. Callers must ask BEFORE writing rather than ask
 * forgiveness afterwards: for the seeded majority there is no error to catch.
 */
export function mastersRestWriteReachesTheRead(): boolean {
  return !isLiveModule('masters');
}

/**
 * Thrown rather than performing an unobservable write. Never rendered raw —
 * the consumer's existing error toast is what the user sees, and an honest
 * error beats a false success.
 */
export class MastersRestWriteUnreachableError extends Error {
  constructor(operation: string) {
    super(
      `${operation} has no graph root wired while the masters reads are graph-served — ` +
        'the write would land in the mock store and the next read would not show it'
    );
    this.name = 'MastersRestWriteUnreachableError';
  }
}

/**
 * The one line each unwired masters write opens with. Named per call site so a
 * thrown error says WHICH write refused, and so the guard can compute the
 * refusing set from source instead of being handed a list.
 */
export function refuseUnobservableMastersWrite(operation: string): void {
  if (!mastersRestWriteReachesTheRead()) {
    throw new MastersRestWriteUnreachableError(operation);
  }
}
